import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

// PermissionGuard component - renders children only if user has required permission
export const PermissionGuard = ({ module, submodule, action, children, fallback = null }) => {
  const { hasPermission } = usePermissions();
  
  if (hasPermission(module, submodule, action)) {
    return children;
  }
  return fallback;
};

// Hook for getting all CRUD permissions at once for a module/submodule
export const useCrudPermissions = (module, submodule) => {
  const { hasPermission, isAdmin } = usePermissions();
  
  return useMemo(() => {
    // Admins have all permissions
    if (isAdmin()) {
      return {
        canCreate: true,
        canView: true,
        canUpdate: true,
        canDelete: true,
        canImport: true,
        isViewOnly: false,
        hasAnyAccess: true
      };
    }
    
    const canCreate = hasPermission(module, submodule, 'create');
    const canView = hasPermission(module, submodule, 'view');
    const canUpdate = hasPermission(module, submodule, 'update');
    const canDelete = hasPermission(module, submodule, 'delete');
    const canImport = hasPermission(module, submodule, 'create'); // Import typically requires create permission
    
    return {
      canCreate,
      canView,
      canUpdate,
      canDelete,
      canImport,
      isViewOnly: canView && !canCreate && !canUpdate && !canDelete,
      hasAnyAccess: canView || canCreate || canUpdate || canDelete
    };
  }, [module, submodule, hasPermission, isAdmin]);
};

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();

  const hasModuleAccess = (moduleId) => {
    console.log('[PermissionContext] Checking access for module:', moduleId);
    console.log('[PermissionContext] User permissions:', user?.module_permissions);
    
    // System admins (no module_permissions) have access to all modules
    if (isAdmin()) {
      console.log('[PermissionContext] System admin - has access to all modules');
      return true;
    }
    
    if (!user?.module_permissions) {
      console.log('[PermissionContext] No module_permissions found, returning false');
      return false;
    }
    
    const modulePerms = user.module_permissions[moduleId];
    if (!modulePerms) {
      console.log('[PermissionContext] No permissions for module:', moduleId);
      return false;
    }
    
    // Check if user has at least one permission (create, view, update, or delete) set to true
    // in any submodule
    const hasAccess = Object.values(modulePerms).some(submodule => 
      submodule.create === true || 
      submodule.view === true || 
      submodule.update === true || 
      submodule.delete === true
    );
    
    console.log('[PermissionContext] Module:', moduleId, 'hasAccess:', hasAccess);
    return hasAccess;
  };

  const hasPermission = (moduleId, submoduleId, action) => {
    // System admins have all permissions
    if (isAdmin()) {
      console.log('[PermissionContext] System admin - has all permissions');
      return true;
    }
    
    if (!user?.module_permissions) return false;
    
    // Map 'read' to 'view' for database compatibility
    const dbAction = action === 'read' ? 'view' : action;
    
    try {
      if (submoduleId) {
        const modulePerms = user.module_permissions[moduleId];
        if (!modulePerms) return false;
        
        // Handle singular/plural mismatch (e.g., 'users' vs 'user', 'leaves' vs 'leave')
        let actualSubmoduleId = submoduleId;
        if (!modulePerms[submoduleId]) {
          // Try singular form
          const singular = submoduleId.endsWith('s') ? submoduleId.slice(0, -1) : submoduleId;
          // Try plural form
          const plural = submoduleId.endsWith('s') ? submoduleId : submoduleId + 's';
          
          if (modulePerms[singular]) {
            actualSubmoduleId = singular;
          } else if (modulePerms[plural]) {
            actualSubmoduleId = plural;
          } else {
            return false;
          }
        }
        
        return modulePerms[actualSubmoduleId]?.[dbAction] === true;
      } else {
        // Check if any submodule has this permission
        const modulePerms = user.module_permissions[moduleId];
        if (!modulePerms) return false;
        
        return Object.values(modulePerms).some(submodule => 
          submodule[dbAction] === true
        );
      }
    } catch (error) {
      console.error('Permission check error:', error);
      return false;
    }
  };

  const hasDepartmentAccess = (department) => {
    if (!user?.department_permissions) return false;
    return user.department_permissions[department]?.access === true;
  };

  const getDepartmentRole = (department) => {
    if (!user?.department_permissions) return null;
    return user.department_permissions[department]?.role;
  };

  const isAdmin = () => {
    // Superadmin users are always admins
    if (user?.role === 'superadmin' || user?.is_superadmin) {
      return true;
    }

    // System admins are users WITHOUT module_permissions (not created through HR)
    // These are self-signup users or direct database users
    if (user && !user.module_permissions) {
      console.log('[PermissionContext] User has no module_permissions - treating as system admin');
      return true;
    }
    
    // If user has module_permissions, they were created through HR
    // Check if they have admin role in any department
    if (user?.department_permissions) {
      const hasAdminRole = Object.values(user.department_permissions).some(
        dept => dept.role === 'admin'
      );
      if (hasAdminRole) {
        console.log('[PermissionContext] User has admin role in department');
        return true;
      }
    }
    
    console.log('[PermissionContext] User is a regular user (not admin)');
    return false;
  };

  // Helper to check if user can perform any write operation (create, update, delete)
  const canWrite = (moduleId, submoduleId) => {
    if (isAdmin()) return true;
    return (
      hasPermission(moduleId, submoduleId, 'create') ||
      hasPermission(moduleId, submoduleId, 'update') ||
      hasPermission(moduleId, submoduleId, 'delete')
    );
  };

  // Helper to check if user is view-only for a module/submodule
  const isViewOnly = (moduleId, submoduleId) => {
    if (isAdmin()) return false;
    const canView = hasPermission(moduleId, submoduleId, 'view');
    const canModify = canWrite(moduleId, submoduleId);
    return canView && !canModify;
  };

  const value = {
    hasModuleAccess,
    hasPermission,
    hasDepartmentAccess,
    getDepartmentRole,
    isAdmin,
    canWrite,
    isViewOnly,
    user
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};
